/**
 * Account Information Section
 *
 * Displays and allows editing of personal information (first name, pronouns, birthday, etc.).
 * Includes profile completion banner when required fields are missing.
 * Conversation partners are managed on the /partners page.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  useCurrentUser,
  useProfileOptions,
  useUpdateCurrentUser,
} from "../../hooks/useCurrentUser";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card } from "../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Check, Loader2, AlertCircle } from "lucide-react";
import {
  checkProfileCompletion,
  formatMissingFields,
} from "../../utils/profileCompletion";
import { DeleteAccountSection } from "./DeleteAccountSection";

interface AccountInfoSectionProps {
  profileIncomplete?: boolean;
  returnTo?: string;
}

export function AccountInfoSection({
  profileIncomplete = false,
  returnTo,
}: AccountInfoSectionProps) {
  const navigate = useNavigate();
  const {
    data: user,
    isLoading: userLoading,
    error: userError,
  } = useCurrentUser();
  const updateUser = useUpdateCurrentUser();
  const { data: profileOptions } = useProfileOptions();

  // User first name
  const [firstName, setFirstName] = useState("");

  // Demographic fields
  const [birthday, setBirthday] = useState("");
  const [genderIdentity, setGenderIdentity] = useState("");
  const [sexualOrientation, setSexualOrientation] = useState("");
  const [pronouns, setPronouns] = useState("");

  const [saveSuccess, setSaveSuccess] = useState(false);

  // Check current profile completion status
  const profileCompletion = checkProfileCompletion(user);

  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || "");
      setBirthday(user.birthday || "");
      setGenderIdentity(user.gender_identity || "");
      setSexualOrientation(user.sexual_orientation || "");
      setPronouns(user.pronouns || "");
    }
  }, [user]);

  const hasChanges =
    firstName !== (user?.first_name || "") ||
    birthday !== (user?.birthday || "") ||
    genderIdentity !== (user?.gender_identity || "") ||
    sexualOrientation !== (user?.sexual_orientation || "") ||
    pronouns !== (user?.pronouns || "");

  const handleSave = async () => {
    try {
      const updatedUser = await updateUser.mutateAsync({
        first_name: firstName || undefined,
        birthday: birthday || null,
        gender_identity: genderIdentity || null,
        sexual_orientation: sexualOrientation || null,
        pronouns: pronouns || null,
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

      // If we were redirected due to incomplete profile and now it's complete, redirect back
      const newCompletion = checkProfileCompletion(updatedUser);
      if (profileIncomplete && newCompletion.isComplete && returnTo) {
        setTimeout(() => {
          navigate(returnTo, { replace: true });
        }, 1000);
      }
    } catch {
      // Error is handled by mutation state
    }
  };

  if (userLoading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse">
          <div className="mb-4 h-6 w-48 rounded bg-muted" />
          <div className="h-4 w-64 rounded bg-muted" />
        </div>
      </Card>
    );
  }

  if (userError) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6">
        <p className="text-destructive">
          Error loading account info: {userError.message}
        </p>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="space-y-6">
      {/* Profile incomplete banner */}
      {profileIncomplete && !profileCompletion.isComplete && (
        <div className="rounded-lg border border-amber-500/50 bg-amber-50 p-4 dark:bg-amber-950/20">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
            <div>
              <h3 className="font-semibold text-amber-800 dark:text-amber-200">
                Complete Your Profile
              </h3>
              <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                Please fill in the required fields below to use the app.
              </p>
              <p className="mt-2 text-sm font-medium text-amber-700 dark:text-amber-300">
                Missing: {formatMissingFields(profileCompletion.missingFields)}
              </p>
            </div>
          </div>
        </div>
      )}

      <Card className="p-6">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-foreground">
            Personal Information
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Your information helps us provide personalized feedback. Fields
            marked with <span className="text-destructive">*</span> are
            required.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="firstName"
              className="block text-sm font-medium text-foreground"
            >
              Your First Name <span className="text-destructive">*</span>
            </label>
            <Input
              id="firstName"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Enter your first name"
              disabled={updateUser.isPending}
              className={`mt-1 ${!firstName.trim() && profileIncomplete ? "border-destructive" : ""}`}
            />
          </div>

          <div>
            <label
              htmlFor="pronouns"
              className="block text-sm font-medium text-foreground"
            >
              Your Pronouns <span className="text-destructive">*</span>
            </label>
            <Select
              value={pronouns}
              onValueChange={setPronouns}
              disabled={updateUser.isPending}
            >
              <SelectTrigger
                id="pronouns"
                className={`mt-1 ${!pronouns && profileIncomplete ? "border-destructive" : ""}`}
              >
                <SelectValue placeholder="Select your pronouns" />
              </SelectTrigger>
              <SelectContent>
                {profileOptions?.pronouns.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label
              htmlFor="birthday"
              className="block text-sm font-medium text-foreground"
            >
              Birthday
            </label>
            <Input
              id="birthday"
              type="date"
              value={birthday}
              onChange={(e) => setBirthday(e.target.value)}
              disabled={updateUser.isPending}
              className="mt-1"
            />
          </div>

          <div>
            <label
              htmlFor="gender_identity"
              className="block text-sm font-medium text-foreground"
            >
              Gender Identity
            </label>
            <Input
              id="gender_identity"
              type="text"
              value={genderIdentity}
              onChange={(e) => setGenderIdentity(e.target.value)}
              placeholder="e.g., Woman, Man, Non-binary, etc."
              disabled={updateUser.isPending}
              className="mt-1"
            />
          </div>

          <div>
            <label
              htmlFor="sexual_orientation"
              className="block text-sm font-medium text-foreground"
            >
              Sexual Orientation
            </label>
            <Select
              value={sexualOrientation}
              onValueChange={setSexualOrientation}
              disabled={updateUser.isPending}
            >
              <SelectTrigger id="sexual_orientation" className="mt-1">
                <SelectValue placeholder="Select your sexual orientation" />
              </SelectTrigger>
              <SelectContent>
                {profileOptions?.sexual_orientations.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <p className="mt-4 text-xs text-muted-foreground">
          This information is private and used only to provide more personalized
          and relevant insights.
        </p>
      </Card>

      <Card className="p-6">
        <h2 className="mb-4 text-lg font-semibold text-foreground">
          Account Details
        </h2>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Email</dt>
            <dd className="text-sm text-foreground">{user.email}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">
              Member since
            </dt>
            <dd className="text-sm text-foreground">
              {new Date(user.created_at).toLocaleDateString()}
            </dd>
          </div>
        </dl>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-4">
          <Button
            onClick={handleSave}
            disabled={!hasChanges || updateUser.isPending}
          >
            {updateUser.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {saveSuccess ? (
              <>
                <Check className="mr-2 h-4 w-4" />
                Saved
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
          {saveSuccess && (
            <span className="text-sm text-green-600 dark:text-green-400">
              Your information has been updated.
            </span>
          )}
          {updateUser.error && (
            <span className="text-sm text-destructive">
              Failed to save. Please try again.
            </span>
          )}
        </div>
      </Card>

      {/* Danger Zone */}
      <DeleteAccountSection />
    </div>
  );
}
